// src/components/BucksWorkerDashboard.jsx
// Updated version with Online Orders integration

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import BucksWorkerOnlineOrder from './BucksWorkerOnlineOrder';
import '../stylings/BucksWorkerDashboard.css';

function BucksWorkerDashboard() {
  // Get authentication context
  const { adminUser, logout } = useAuth();
  
  // Current time state
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Worker status state
  const [workerStatus, setWorkerStatus] = useState('clocked-out'); // 'clocked-in', 'clocked-out', 'on-break'
  
  // Time tracking state
  const [clockInTime, setClockInTime] = useState(null);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [todayHours, setTodayHours] = useState(0);
  const [weekHours, setWeekHours] = useState(0);
  
  // Loading state for API calls
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  // Notification state
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Active section state - Updated to include 'orders'
  const [activeSection, setActiveSection] = useState('timecard');
  
  // API endpoint constants
  const PUNCH_API_ENDPOINT = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-worker-punch-in';
  const PUNCH_INFO_API_ENDPOINT = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-worker-punch-info-get';
  
  // Punch history data (daily summaries)
  const [punchHistory, setPunchHistory] = useState([]);
  
  // Recent punches data (individual punch events)
  const [recentPunches, setRecentPunches] = useState([]);
  
  // Worker data from API
  const [workerData, setWorkerData] = useState(null);

  // Format time for CDT display
  const formatCDTTime = (utcTimeString) => {
    if (!utcTimeString) return '';
    
    // Create date from UTC string and format in CDT
    const utcDate = new Date(utcTimeString);
    return utcDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Chicago'
    });
  };

  // Format time for status details
  const formatTimeShort = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Chicago'
    });
  };

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Load worker data from API
  const loadWorkerData = async () => {
    if (!adminUser?.id) {
      console.log('No user ID available yet');
      return;
    }

    setIsLoadingData(true);
    
    try {
      const response = await fetch(`${PUNCH_INFO_API_ENDPOINT}?user_id=${adminUser.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to load worker data');
      }

      if (result.success) {
        const data = result.data;
        
        // Update worker data
        setWorkerData(data);
        
        // Update status based on API response
        const currentStatus = data.current_status;
        if (currentStatus.status === 'clocked_in') {
          setWorkerStatus('clocked-in');
          if (currentStatus.last_punch_time) {
            // The time from API is already in CDT ISO format
            setClockInTime(new Date(currentStatus.last_punch_time));
          }
        } else if (currentStatus.status === 'on_break') {
          setWorkerStatus('on-break');
          if (currentStatus.last_punch_time) {
            // The time from API is already in CDT ISO format
            setBreakStartTime(new Date(currentStatus.last_punch_time));
          }
        } else {
          setWorkerStatus('clocked-out');
          setClockInTime(null);
          setBreakStartTime(null);
        }
        
        // Update hours
        setTodayHours(data.today.total_hours || 0);
        setWeekHours(data.this_week.total_hours || 0);
        
        // Update punch history with recent daily summaries
        setPunchHistory(data.recent_daily_summaries.map(summary => ({
          id: summary.work_date,
          date: summary.work_date,
          clockIn: summary.clock_in || 'N/A',
          clockOut: summary.clock_out || 'N/A',
          breakDuration: summary.break_duration,
          totalHours: summary.formatted_hours,
          status: summary.status
        })));
        
        // Update recent punches (individual punch events)
        setRecentPunches(data.recent_punches || []);
        
        console.log('Worker data loaded successfully:', data);
      }
    } catch (error) {
      console.error('Failed to load worker data:', error);
      showNotification('Failed to load worker data. Using default values.', 'warning');
    } finally {
      setIsLoadingData(false);
    }
  };

  // Initialize worker data
  useEffect(() => {
    loadWorkerData();
  }, [adminUser?.id]);

  // Calculate elapsed time since clock in
  const getElapsedTime = useCallback(() => {
    if (!clockInTime) return '00:00:00';
    
    const now = new Date();
    const elapsed = now - clockInTime;
    const hours = Math.floor(elapsed / (1000 * 60 * 60));
    const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((elapsed % (1000 * 60)) / 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [clockInTime]);

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  // API call helper function
  const makeApiCall = async (punchType, location = 'Farm Location', notes = '') => {
    if (!adminUser?.id) {
      throw new Error('User ID not available');
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(PUNCH_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: adminUser.id,
          punch_type: punchType,
          location: location,
          notes: notes,
          device_info: navigator.userAgent
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'API call failed');
      }

      return result;
    } catch (error) {
      console.error('API call error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle clock in
  const handleClockIn = async () => {
    try {
      const result = await makeApiCall('clock_in', 'Farm Entrance', 'Starting workday');
      
      if (result.success) {
        const punchTime = new Date(result.data.punch_time);
        setWorkerStatus('clocked-in');
        setClockInTime(punchTime);
        
        // Reload worker data to get updated status and hours
        await loadWorkerData();
        
        // Show notification with CDT time
        const cdtTimeDisplay = formatCDTTime(result.data.punch_time);
        showNotification(`Welcome! You're clocked in as of ${cdtTimeDisplay}`, 'success');
      }
    } catch (error) {
      showNotification(error.message || 'Failed to clock in. Please try again.', 'error');
    }
  };

  // Handle clock out
  const handleClockOut = async () => {
    try {
      const result = await makeApiCall('clock_out', 'Farm Entrance', 'End of workday');
      
      if (result.success) {
        const now = new Date();
        const elapsed = clockInTime ? (now - clockInTime) / (1000 * 60 * 60) : 0; // hours
        
        setWorkerStatus('clocked-out');
        setClockInTime(null);
        setBreakStartTime(null);
        
        // Reload worker data to get updated status and hours
        await loadWorkerData();
        
        showNotification(`Day complete! You worked ${elapsed.toFixed(1)} hours today. Thank you for your dedication.`, 'success');
      }
    } catch (error) {
      showNotification(error.message || 'Failed to clock out. Please try again.', 'error');
    }
  };

  // Handle break start
  const handleBreakStart = async () => {
    try {
      const result = await makeApiCall('break_start', 'Break Room', 'Taking a break');
      
      if (result.success) {
        const punchTime = new Date(result.data.punch_time);
        setWorkerStatus('on-break');
        setBreakStartTime(punchTime);
        
        // Reload worker data to get updated status
        await loadWorkerData();
        
        // Show notification with CDT time
        const cdtTimeDisplay = formatCDTTime(result.data.punch_time);
        showNotification(`Enjoy your break! Started at ${cdtTimeDisplay}`, 'info');
      }
    } catch (error) {
      showNotification(error.message || 'Failed to start break. Please try again.', 'error');
    }
  };

  // Handle break end
  const handleBreakEnd = async () => {
    try {
      const result = await makeApiCall('break_end', 'Break Room', 'Returning from break');
      
      if (result.success) {
        setWorkerStatus('clocked-in');
        setBreakStartTime(null);
        
        // Reload worker data to get updated status
        await loadWorkerData();
        
        showNotification('Welcome back! Hope you had a refreshing break.', 'success');
      }
    } catch (error) {
      showNotification(error.message || 'Failed to end break. Please try again.', 'error');
    }
  };

  // Format time with elegant styling
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Format date with refined presentation
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get status color class
  const getStatusClass = () => {
    switch (workerStatus) {
      case 'clocked-in':
        return 'status-clocked-in';
      case 'on-break':
        return 'status-on-break';
      case 'clocked-out':
      default:
        return 'status-clocked-out';
    }
  };

  // Get status display text
  const getStatusText = () => {
    switch (workerStatus) {
      case 'clocked-in':
        return 'Clocked In';
      case 'on-break':
        return 'On Break';
      case 'clocked-out':
      default:
        return 'Clocked Out';
    }
  };

  // Coming Soon Component
  const ComingSoonSection = ({ title, description, icon }) => (
    <div className="coming-soon-section">
      <div className="coming-soon-content">
        <div className="coming-soon-icon">
          {icon}
        </div>
        <div className="coming-soon-text">
          <h2 className="coming-soon-title">{title}</h2>
          <p className="coming-soon-description">{description}</p>
          <div className="coming-soon-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12,6 12,12 16,14"/>
            </svg>
            Coming Soon
          </div>
        </div>
      </div>
      <div className="coming-soon-animation">
        <div className="pulse-ring"></div>
        <div className="pulse-ring delay-1"></div>
        <div className="pulse-ring delay-2"></div>
      </div>
    </div>
  );

  // Show loading state
  if (isLoadingData) {
    return (
      <div className="bucks-worker-dashboard">
        <div className="worker-main" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.2rem', color: '#a67c52' }}>Loading worker data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bucks-worker-dashboard">
      {/* Mobile Header */}
      <header className="worker-header">
        <div className="header-content">
          <div className="header-title">
            <h1 className="worker-logo">Buck's Farmstand</h1>
            <p className="worker-subtitle">Worker Portal</p>
          </div>
          <div className="header-user">
            <div className="user-avatar">
              <span>{adminUser?.username?.charAt(0)?.toUpperCase() || 'W'}</span>
            </div>
            <div className="user-info">
              <p className="user-name">{adminUser?.username || 'Worker'}</p>
              <p className="user-role">Farm Worker</p>
            </div>
          </div>
        </div>
      </header>

      {/* Notification */}
      {notification.show && (
        <div className={`worker-notification ${notification.type}`}>
          <span className="notification-message">{notification.message}</span>
          <button 
            className="close-notification" 
            onClick={() => setNotification({ show: false, message: '', type: '' })}
          >
            ×
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="worker-main">
        {activeSection === 'timecard' && (
          <div className="timecard-section">
            {/* Current Time Display */}
            <div className="current-time-card">
              <div className="time-display">
                <div className="current-time">{formatTime(currentTime)}</div>
                <div className="current-date">{formatDate(currentTime)}</div>
              </div>
            </div>

            {/* Status Card */}
            <div className="status-card">
              <div className="status-header">
                <div className={`status-indicator ${getStatusClass()}`}></div>
                <div className="status-info">
                  <h2 className="status-title">{getStatusText()}</h2>
                  {workerStatus === 'clocked-in' && clockInTime && (
                    <p className="status-detail">
                      Since {formatTimeShort(clockInTime)} • {getElapsedTime()}
                    </p>
                  )}
                  {workerStatus === 'on-break' && breakStartTime && (
                    <p className="status-detail">
                      Break started at {formatTimeShort(breakStartTime)}
                    </p>
                  )}
                  {workerStatus === 'clocked-out' && (
                    <p className="status-detail">
                      {workerData?.current_status?.description || 'Ready to begin your workday'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Time Card Actions */}
            <div className="timecard-actions">
              {workerStatus === 'clocked-out' && (
                <button 
                  className="action-btn clock-in-btn"
                  onClick={handleClockIn}
                  disabled={isLoading}
                >
                  <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                  {isLoading ? 'Clocking In...' : 'Begin Workday'}
                </button>
              )}
              
              {workerStatus === 'clocked-in' && (
                <>
                  <button 
                    className="action-btn break-btn"
                    onClick={handleBreakStart}
                    disabled={isLoading}
                  >
                    <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 6v6l4 2"/>
                    </svg>
                    {isLoading ? 'Starting Break...' : 'Take Break'}
                  </button>
                  <button 
                    className="action-btn clock-out-btn"
                    onClick={handleClockOut}
                    disabled={isLoading}
                  >
                    <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M10 2v20M14 2v20"/>
                    </svg>
                    {isLoading ? 'Clocking Out...' : 'End Workday'}
                  </button>
                </>
              )}
              
              {workerStatus === 'on-break' && (
                <>
                  <button 
                    className="action-btn return-btn"
                    onClick={handleBreakEnd}
                    disabled={isLoading}
                  >
                    <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 12h18m-9-9l9 9-9 9"/>
                    </svg>
                    {isLoading ? 'Returning...' : 'Return to Work'}
                  </button>
                  <button 
                    className="action-btn clock-out-btn"
                    onClick={handleClockOut}
                    disabled={isLoading}
                  >
                    <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M10 2v20M14 2v20"/>
                    </svg>
                    {isLoading ? 'Clocking Out...' : 'End Workday'}
                  </button>
                </>
              )}
            </div>

            {/* Hours Summary */}
            <div className="hours-summary">
              <div className="summary-card">
                <div className="summary-icon today-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <div className="summary-content">
                  <h3>Today</h3>
                  <p className="summary-value">{todayHours.toFixed(1)}h</p>
                </div>
              </div>
              
              <div className="summary-card">
                <div className="summary-icon week-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 2v4M16 2v4"/>
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <path d="M3 10h18"/>
                    <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/>
                  </svg>
                </div>
                <div className="summary-content">
                  <h3>This Week</h3>
                  <p className="summary-value">{weekHours.toFixed(1)}h</p>
                </div>
              </div>
            </div>

            {/* Recent Punch History - Now showing individual punch events */}
            <div className="punch-history">
              <h3 className="history-title">Recent Punches</h3>
              <div className="history-list">
                {recentPunches.length > 0 ? recentPunches.slice(0, 5).map((punch, index) => (
                  <div key={`${punch.punch_date}-${punch.punch_time}-${index}`} className="history-item punch-event">
                    <div className="punch-type-icon">
                      <span className={`punch-icon punch-${punch.punch_type}`}>
                        {punch.punch_type === 'clock_in' && '⏰'}
                        {punch.punch_type === 'clock_out' && '🏁'}
                        {punch.punch_type === 'break_start' && '☕'}
                        {punch.punch_type === 'break_end' && '▶️'}
                      </span>
                    </div>
                    <div className="punch-details">
                      <div className="punch-type-display">
                        {punch.punch_type_display}
                      </div>
                      <div className="punch-meta">
                        <span className="punch-time">{punch.formatted_time}</span>
                        <span className="punch-date">{punch.formatted_date}</span>
                        <span className="punch-location">{punch.location}</span>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#8a7a64' }}>
                    No recent punch records available
                  </div>
                )}
              </div>
            </div>

            {/* Daily Summary History */}
            <div className="daily-summary">
              <h3 className="history-title">Daily Summary</h3>
              <div className="history-list">
                {punchHistory.length > 0 ? punchHistory.slice(0, 3).map((punch, index) => (
                  <div key={punch.id || index} className="history-item">
                    <div className="history-date">
                      <span className="date-text">{new Date(punch.date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}</span>
                    </div>
                    <div className="history-details">
                      <div className="time-range">
                        {punch.clockIn} - {punch.clockOut}
                      </div>
                      <div className="history-meta">
                        <span className="total-hours">{punch.totalHours}</span>
                        <span className="break-time">Break: {punch.breakDuration}</span>
                      </div>
                    </div>
                    <div className={`history-status status-${punch.status}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    </div>
                  </div>
                )) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#8a7a64' }}>
                    No daily summary history available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Online Orders Section - New! */}
        {activeSection === 'orders' && (
          <BucksWorkerOnlineOrder />
        )}

        {activeSection === 'schedule' && (
          <ComingSoonSection 
            title="Work Schedule"
            description="Your personalized work schedule, shift assignments, and upcoming events will be available here. Stay tuned for updates on your scheduled hours and important farm activities."
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
                <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/>
              </svg>
            }
          />
        )}

        {activeSection === 'tasks' && (
          <ComingSoonSection 
            title="Daily Tasks"
            description="Your assigned tasks, work orders, and priority activities will appear here. This feature will help you stay organized and track your daily responsibilities on the farm."
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
            }
          />
        )}
      </main>

      {/* Bottom Navigation - Updated with Orders tab */}
      <nav className="worker-bottom-nav">
        <button 
          className={`nav-item ${activeSection === 'timecard' ? 'active' : ''}`}
          onClick={() => setActiveSection('timecard')}
        >
          <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12,6 12,12 16,14"/>
          </svg>
          <span>Timecard</span>
        </button>
        
        <button 
          className={`nav-item ${activeSection === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveSection('orders')}
        >
          <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 11V7a4 4 0 0 0-8 0v4H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-3z"/>
          </svg>
          <span>Orders</span>
        </button>
        
        <button 
          className={`nav-item ${activeSection === 'schedule' ? 'active' : ''}`}
          onClick={() => setActiveSection('schedule')}
        >
          <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span>Schedule</span>
        </button>
        
        <button 
          className={`nav-item ${activeSection === 'tasks' ? 'active' : ''}`}
          onClick={() => setActiveSection('tasks')}
        >
          <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10,9 9,9 8,9"/>
          </svg>
          <span>Tasks</span>
        </button>
        
        <button 
          className="nav-item"
          onClick={logout}
        >
          <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          <span>Logout</span>
        </button>
      </nav>
    </div>
  );
}

export default BucksWorkerDashboard;